import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = [
    "button",
    "label",
    "deviceSelect",
    "trash",
    "icon",
    "spinner",
  ];
  static values = {
    url: String,
    tapToSpeak: String,
    listening: String,
    sending: String,
    tooShort: String,
    networkError: String,
    micDenied: String,
  };

  connect() {
    this.recording = false;
    this.chunks = [];
    this.boundCleanup = this.cleanup.bind(this);
    window.addEventListener("beforeunload", this.boundCleanup);
    this.loadDevices();
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundCleanup);
    this.cleanup();
  }

  cleanup() {
    if (this.recording && this.recorder) {
      this.recorder.onstop = () => { };
      this.recorder.stop();
      this.recorder.stream.getTracks().forEach((t) => t.stop());
    }
  }

  async loadDevices() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === "audioinput");

      this.deviceSelectTarget.innerHTML = mics
        .map(
          (d) =>
            `<option value="${d.deviceId}">${d.label || "Microphone " + d.deviceId}</option>`,
        )
        .join("");
    } catch (err) {
      this.setLabel(this.micDeniedValue);
      console.error("loadDevices failed: ", err);
    }
  }

  async toggle() {
    if (this.transitioning) return;
    this.transitioning = true;
    this.recording ? this.stop() : this.start();
    this.transitioning = false;
  }

  async start() {
    try {
      const deviceId = this.deviceSelectTarget.value;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });

      this.recorder = new MediaRecorder(stream);
      this.chunks = [];

      this.recorder.ondataavailable = (e) => this.chunks.push(e.data);
      this.recorder.onstop = () => this.submit();
      this.recorder.start();

      this.recording = true;

      this.buttonTarget.classList.add("btn-soft");
      this.setLabel(this.listeningValue);
      this.trashTarget.disabled = false;
      this.trashTarget.classList.remove("btn-soft");
    } catch (err) {
      this.setLabel(this.micDeniedValue, 3000);
      console.error("start failed: ", err);
    }
  }

  async stop() {
    this.recorder.stop();
    this.recorder.stream.getTracks().forEach((t) => t.stop());
    this.recording = false;

    this.showSpinner();
    this.buttonTarget.classList.remove("btn-soft");
    this.trashTarget.classList.add("btn-soft");
    this.trashTarget.disabled = true;
    this.setLabel(this.tapToSpeakValue);
  }

  cancel() {
    if (!this.recording) return;
    // stop tracks but skip submit()
    this.recorder.onstop = () => { };
    this.recorder.stop();
    this.recorder.stream.getTracks().forEach((t) => t.stop());
    this.recording = false;
    this.chunks = [];

    this.buttonTarget.classList.remove("btn-soft");
    this.setLabel(this.tapToSpeakValue);
    this.trashTarget.disabled = true;
    this.trashTarget.classList.add("btn-soft");
  }

  async submit() {
    const blob = new Blob(this.chunks, { type: "audio/webm" });

    if (blob.size < 2000) {
      this.hideSpinner();
      this.setLabel(this.tooShortValue, 2000);
      return;
    }

    this.setLabel(this.sendingValue);

    const formData = new FormData();
    formData.append("audio", blob, "recording.webm");
    formData.append("content_type", "audio/webm");
    formData.append(
      "authenticity_token",
      document.querySelector('meta[name="csrf-token"]').content,
    );

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: formData,
      });

      if (!response.ok) {
        this.hideSpinner();
        this.setLable("failed, try again", 2000);
        return;
      }

      const html = await response.text();
      Turbo.renderStreamMessage(html);
      this.hideSpinner();
      this.setLabel(this.tapToSpeakValue);
    } catch (err) {
      this.hideSpinner();
      this.setLabel(this.networkErrorValue, 2000);
      console.error("submit failed: ", err);
    }
  }

  showSpinner() {
    this.buttonTarget.disabled = true;
    this.iconTarget.classList.add("hidden");
    this.spinnerTarget.classList.remove("hidden");
  }

  hideSpinner() {
    this.buttonTarget.disabled = false;
    this.iconTarget.classList.remove("hidden");
    this.spinnerTarget.classList.add("hidden");
  }

  setLabel(text, resetAfterMs = null) {
    this.labelTarget.textContent = text;
    if (resetAfterMs) {
      setTimeout(
        () => (this.labelTarget.textContent = this.tapToSpeakValue),
        resetAfterMs,
      );
    }
  }
}
